import axios, { AxiosError, AxiosHeaders } from 'axios'
import camelcaseKeys from 'camelcase-keys'
import { useAuthStore } from '@/stores/auth'
import { useApiStatusStore } from '@/modules/api-status/stores/api-status.store'
import { setCookie } from '@/services/cookies'

type ApiFilterValue = string | number | Date | string[]

export const API_URL = import.meta.env.VITE_API_URL
export const ERROR_NETWORK = 'Something went wrong. Please try again.'
export const SERVER_ERROR = 'Something went wrong and has been reported.'
export const SERVER_VALIDATION_ERROR = "That didn't work. Please check and try again."
export const UNAUTHORIZED_ERROR = 'You are not authorized to access this resource.'
export const enum REQUEST_METHODS {
  GET = 'get',
  POST = 'post',
  PATCH = 'patch',
  DELETE = 'delete'
}
export const enum STATUS_CODES {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  UNPROCESSABLE_ENTITY = 422,
  INTERNAL_SERVER = 500,
  NETWORK_ERROR = 0
}
export enum FILTER_OPERANDS {
  EQUAL = '==',
  INCLUDES = 'ilike',
  LESS_THAN = '<',
  GREATER_THAN = '>',
  LESS_THAN_EQUAL = '<=',
  GREATER_THAN_EQUAL = '>=',
  IN = 'in',
  NOT_IN = 'not_in',
  NOT = 'not'
}
export enum ACCOUNT_STATUS_ACTIONS {
  ACTIVATE = 'activate',
  DEACTIVATE = 'deactivate'
}

export enum ORDER_DIRECTIONS {
  ASC = 'asc',
  DESC = 'desc'
}

function buildAuthHeader(): AxiosHeaders {
  const authStore = useAuthStore()
  const token = authStore.getToken()
  if (token) {
    return new AxiosHeaders({ authorization: `Bearer ${token}` })
  } else {
    return new AxiosHeaders()
  }
}

/**
 * @deprecated Use doRequest instead
 */
export const doRequestOld = async (method: string, endpoint: string, data: any = {}): Promise<any> => {
  try {
    const result = await axios.request({
      method,
      url: `${API_URL}${endpoint}`,
      data: data,
      headers: buildAuthHeader()
    })

    return result.data
  } catch (error: any) {
    if (isNetworkOrTimeoutError(error.code)) {
      const apiStatusStore = useApiStatusStore()
      apiStatusStore.setApiAvailability(false)
      return { error: ERROR_NETWORK, errorCode: error.code, data: [] }
    } else if (error.response?.status) {
      const base = {
        errorCode: error.code,
        status_code: error.response.status,
        server_error: error.response.data?.error,
        ...(error.response.data || {})
      }

      if (error.response.status >= STATUS_CODES.INTERNAL_SERVER) {
        return { ...base, error: ERROR_NETWORK }
      } else if (error.response.status == STATUS_CODES.UNPROCESSABLE_ENTITY) {
        return { ...base, error: SERVER_VALIDATION_ERROR }
      } else if (
        error.response.status >= STATUS_CODES.BAD_REQUEST &&
        error.response.status != STATUS_CODES.UNAUTHORIZED
      ) {
        return { ...base, error: SERVER_ERROR }
      } else if (error.response.status == STATUS_CODES.UNAUTHORIZED) {
        // Handle redirect asynchronously to avoid circular dependency
        handleUnauthorizedRedirect()
        return { ...base, error: UNAUTHORIZED_ERROR }
      } else {
        return { ...base, error: 'Something went wrong' }
      }
    } else {
      return { error: ERROR_NETWORK, errorCode: error.code }
    }
  }
}

export const doRequest = async <T = any>(method: string, endpoint: string, data: any = {}): Promise<T> => {
  let response: any

  try {
    response = await axios.request({
      method,
      url: `${API_URL}${endpoint}`,
      data: data,
      headers: buildAuthHeader()
    })
  } catch (error: any) {
    let errorToThrown = {
      errors: [{ code: 'network_error', message: ERROR_NETWORK }],
      statusCode: STATUS_CODES.INTERNAL_SERVER
    }

    if (isNetworkOrTimeoutError(error.code)) {
      const apiStatusStore = useApiStatusStore()
      apiStatusStore.setApiAvailability(false)

      errorToThrown = {
        errors: [{ code: 'network_error', message: ERROR_NETWORK }],
        statusCode: STATUS_CODES.NETWORK_ERROR
      }
    } else if (error.response?.status) {
      if (error.response.status >= STATUS_CODES.INTERNAL_SERVER) {
        errorToThrown = {
          errors: [{ code: 'internal_server_error', message: SERVER_ERROR }],
          statusCode: error.response.status
        }
      } else if (error.response.status == STATUS_CODES.UNAUTHORIZED) {
        // Handle redirect asynchronously to avoid circular dependency
        handleUnauthorizedRedirect()

        errorToThrown = {
          errors: [{ code: 'unauthorized', message: UNAUTHORIZED_ERROR }],
          statusCode: error.response.status
        }
      } else {
        errorToThrown = {
          errors: error.response?.data?.errors ?? [{ code: 'unknown_error', message: SERVER_ERROR }],
          statusCode: error.response.status
        }
      }
    }

    throw errorToThrown
  }

  if (response.data.success) {
    return camelcaseKeys(response.data, { deep: true }) as T
  } else {
    throw {
      errors: response.data.errors,
      statusCode: response.data.status_code
    }
  }
}

export const pathWithPageAndFilters = (
  base: string,
  filters: ApiFilter[] = [],
  page: ApiPage = null,
  pageSize: number = 10,
  scopedBy?: string,
  scopedId?: string,
  orderBy?: string,
  orderDirection?: string
): string => {
  let url = base
  const params: string[] = []

  if (scopedBy && scopedId) {
    url += `?scoped_by=${scopedBy}&scoped_id=${scopedId}`
  }

  if (page) {
    params.push(`page=${page}`)
  }

  params.push(`page_size=${pageSize}`)

  if (filters.length > 0) {
    params.push(buildFilters(filters))
  }

  if (orderBy && orderDirection) {
    params.push(buildOrder([{ field: orderBy, direction: orderDirection as ORDER_DIRECTIONS }]))
  }

  if (params.length > 0) {
    url += (url.includes('?') ? '&' : '?') + params.join('&')
  }

  return url
}

const buildFilter = (filter: ApiFilter, index: number) => {
  let values = `filters[${index}][value]=${encodeValue(filter.value)}`

  if (filter.op == FILTER_OPERANDS.IN || filter.op == FILTER_OPERANDS.NOT_IN) {
    values = (filter.value as string[]).map((value) => `filters[${index}][value][]=${encodeValue(value)}`).join('&')
  }

  return `filters[${index}][field]=${filter.field}&filters[${index}][op]=${filter.op}&${values}`
}

export const buildFilters = (filters: ApiFilter[] = []) => {
  const filtersToStrings = filters.map((filter, index) => buildFilter(filter, index))
  return filtersToStrings.join('&')
}

export const buildOrder = (orders: { field: string; direction: ORDER_DIRECTIONS }[] = []): string => {
  if (orders.length === 0) return ''

  const orderByParams = orders.map((order) => `order_by[]=${encodeURIComponent(order.field)}`).join('&')
  const orderDirectionParams = orders.map((order) => `order_directions[]=${order.direction}`).join('&')

  return `${orderByParams}&${orderDirectionParams}`
}

/**
 * @deprecated Use doPost instead
 */
export const doPostOld = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.POST, endpoint, data)
}

/**
 * @deprecated Use doGet instead
 */
export const doGetOld = async (endpoint: string): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.GET, endpoint)
}

/**
 * @deprecated Use doPatch instead
 */
export const doPatchOld = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.PATCH, endpoint, data)
}

/**
 * @deprecated Use doDelete instead
 */
export const doDeleteOld = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequestOld(REQUEST_METHODS.DELETE, endpoint, data)
}

export const doGet = async <T = any>(endpoint: string): Promise<T> => {
  return doRequest<T>(REQUEST_METHODS.GET, endpoint)
}

export const doPost = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequest(REQUEST_METHODS.POST, endpoint, data)
}

export const doPatch = async (endpoint: string, data: any = {}): Promise<any> => {
  return doRequest(REQUEST_METHODS.PATCH, endpoint, data)
}

export const doDelete = async (endpoint: string): Promise<any> => {
  return doRequest(REQUEST_METHODS.DELETE, endpoint)
}

export const dollarsToCents = (amount: number): number => {
  return Math.round(amount * 100.0)
}

function encodeValue(value: ApiFilterValue | undefined): string {
  return value !== null && value !== undefined ? encodeURIComponent(value.toString()) : ''
}

export const isNetworkOrTimeoutError = (errorCode: any): boolean => {
  return (
    errorCode === AxiosError.ERR_NETWORK || errorCode === AxiosError.ECONNABORTED || errorCode === AxiosError.ETIMEDOUT
  )
}

/**
 * Handles unauthorized redirect using dynamic imports to avoid circular dependency
 */
async function handleUnauthorizedRedirect(): Promise<void> {
  const authStore = useAuthStore()
  authStore.clear()
  setCookie('signin-expired', 'true')

  // Use dynamic imports to avoid circular dependency
  const { default: router } = await import('@/router')
  const { ROUTES } = await import('@/router/routes.enum')

  // Don't redirect if already on the sign-in page
  if (router.currentRoute.value.name !== ROUTES.SIGN_IN) {
    router.push({ name: ROUTES.SIGN_IN, query: { redirect_to: window.location.pathname } })
  }
}

export default {
  doGetOld,
  doPostOld,
  doPatchOld,
  doDeleteOld,
  doGet,
  doPost,
  doPatch,
  doDelete,
  pathWithPageAndFilters,
  isNetworkOrTimeoutError
}
